import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';

const MESES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
];

// Convierte 'YYYY-MM-DD' al lunes de esa semana ('YYYY-MM-DD')
const getLunesSemana = (fecha) => {
  const d = new Date(`${fecha}T00:00:00`);
  const dia = d.getDay(); // 0 = domingo ... 6 = sábado
  const diff = (dia === 0 ? -6 : 1) - dia;
  d.setDate(d.getDate() + diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const LineaTiempoMenores = ({ data, isLoading }) => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [modo, setModo] = React.useState('dia'); // 'dia' | 'semana' | 'mes'

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Agrupa los datos diarios según el modo seleccionado
  const chartData = React.useMemo(() => {
    const diario = [...(data || [])].sort((a, b) => a.fecha.localeCompare(b.fecha));

    if (modo === 'dia') {
      return diario.map(({ fecha, cantidad }) => {
        const [y, m, d] = fecha.split('-');
        return { key: fecha, label: `${d}/${m}/${y.slice(2)}`, cantidad };
      });
    }

    const mapa = {};
    diario.forEach(({ fecha, cantidad }) => {
      const key = modo === 'mes' ? fecha.slice(0, 7) : getLunesSemana(fecha);
      mapa[key] = (mapa[key] || 0) + cantidad;
    });

    return Object.entries(mapa)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, cantidad]) => {
        let label;
        if (modo === 'mes') {
          const [y, m] = key.split('-');
          label = `${MESES[Number(m) - 1]} ${y.slice(2)}`;
        } else {
          const [y, m, d] = key.split('-');
          label = `${d}/${m}/${y.slice(2)}`;
        }
        return { key, label, cantidad };
      });
  }, [data, modo]);

  const total = React.useMemo(
    () => (data || []).reduce((acc, curr) => acc + curr.cantidad, 0),
    [data]
  );
  const promedio = chartData.length ? Math.round(total / chartData.length) : 0;
  const pico = chartData.reduce(
    (max, curr) => (curr.cantidad > max.cantidad ? curr : max),
    { cantidad: 0, label: '-' }
  );

  const modos = [
    { id: 'dia', label: 'Día' },
    { id: 'semana', label: 'Semana' },
    { id: 'mes', label: 'Mes' }
  ];

  const card = {
    background: '#fff',
    border: '1px solid #e2e6ee',
    borderRadius: '12px',
    padding: isMobile ? '16px' : '18px 20px 12px'
  };

  if (isLoading) {
    return (
      <div style={card}>
        <div style={{ width: isMobile ? 200 : 260, height: 18, borderRadius: 5, background: '#eef1f5', animation: 'pulseCard 1.4s ease-in-out infinite' }} />
        <div style={{ width: '100%', height: isMobile ? 200 : 230, marginTop: 18, background: '#f4f6f8', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 44, height: 44, border: '3px solid #e2e8f0', borderTop: '3px solid #0f766e', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  // Celda del bloque de KPIs (TOTAL / PROMEDIO / PICO) con separadores
  const stat = (label, value, extra) => (
    <div style={{ padding: isMobile ? '8px 12px' : '9px 16px' }}>
      <div style={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '.06em', color: '#8a94a6' }}>{label}</div>
      <div style={{ fontSize: '16px', fontWeight: 600, color: '#0f172a', marginTop: 2, whiteSpace: 'nowrap' }}>
        {value}
        {extra && <span style={{ fontSize: 12, fontWeight: 500, color: '#8a94a6' }}> · {extra}</span>}
      </div>
    </div>
  );

  const separador = <div style={{ width: 1, background: '#eef1f5' }} />;

  return (
    <div style={card}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: isMobile ? 12 : 20,
        marginBottom: 14,
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: isMobile ? 0 : 260 }}>
          <div style={{ fontSize: '15.5px', fontWeight: 600, color: '#0f172a' }}>¿Cuántas convocatorias se publican diariamente?</div>
          <div style={{ fontSize: '12.5px', color: '#5b6779', marginTop: 3 }}>Contratos menores · cantidad de convocatorias diarias</div>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'stretch',
          background: '#fafbfc',
          border: '1px solid #eef1f5',
          borderRadius: 10,
          overflow: 'hidden'
        }}>
          {stat('TOTAL', total.toLocaleString('es-PE'))}
          {separador}
          {stat('PROMEDIO', promedio.toLocaleString('es-PE'))}
          {separador}
          {stat('PICO', pico.cantidad.toLocaleString('es-PE'), pico.label)}
        </div>
        <div style={{ display: 'flex', gap: 3, padding: 3, borderRadius: 9, background: '#f4f6f8' }}>
          {modos.map((m) => {
            const activo = modo === m.id;
            return (
              <button
                key={m.id}
                className={`dash-seg-btn${activo ? ' dash-seg-btn--activo' : ''}`}
                onClick={() => setModo(m.id)}
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 12.5,
                  fontWeight: activo ? 600 : 500,
                  color: activo ? '#0f172a' : '#5b6779',
                  background: activo ? '#fff' : 'transparent',
                  borderRadius: 7,
                  padding: '6px 14px',
                  boxShadow: activo ? '0 1px 2px rgba(15,23,42,.12)' : 'none'
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <div style={{ height: isMobile ? 200 : 230, position: 'relative' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: isMobile ? 10 : 16, left: isMobile ? -15 : 0, bottom: 5 }}
            >
              <defs>
                <linearGradient id="areaMenores" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0f766e" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#0f766e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#f1f4f8" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: isMobile ? 10 : 11, fill: '#8a94a6' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e6ee' }}
                interval="preserveStartEnd"
                minTickGap={isMobile ? 20 : 30}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: isMobile ? 10 : 11, fill: '#8a94a6' }}
                tickLine={false}
                axisLine={false}
                width={isMobile ? 30 : 40}
              />
              <Tooltip
                cursor={{ stroke: '#0f766e', strokeWidth: 1, strokeDasharray: '4 4' }}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e6ee',
                  borderRadius: 8,
                  boxShadow: '0 6px 16px rgba(15,23,42,.08)',
                  padding: '8px 12px',
                  fontSize: 12
                }}
                labelStyle={{ color: '#0f172a', fontWeight: 600, marginBottom: 4 }}
                itemStyle={{ color: '#0f766e', fontWeight: 600 }}
                formatter={(value) => [`${value.toLocaleString('es-PE')} convocatorias`, '']}
              />
              <Area
                type="monotone"
                dataKey="cantidad"
                stroke="#0f766e"
                strokeWidth={2.5}
                fill="url(#areaMenores)"
                dot={false}
                activeDot={{ r: 4, fill: '#0f766e', stroke: '#fff', strokeWidth: 2 }}
                animationDuration={900}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={{ height: isMobile ? 200 : 230, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>
          No hay convocatorias con fecha de publicación disponible
        </div>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulseCard { 0%, 100% { opacity: 1; } 50% { opacity: .55; } }
      `}</style>
    </div>
  );
};

export default LineaTiempoMenores;
